import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "./context/AuthContext";
// Because the cookie is HttpOnly, JavaScript cannot "read" it (which is great 
// for security), but Axios needs to be told to send it with every request 
// to your PHP API.

import "./App.css";

import HomePage from "./pages/HomePage"
import RoutePage from "./pages/RoutePage"
import LoadsheetPage from "./pages/LoadsheetPage";
import InFlightPage from "./pages/InFlightPage";
import OperationsPage from "./pages/OperationsPage";
import JumpseatPage from "./pages/JumpseatPage";
import ChartersViewPage from "./pages/ChartersViewPage";
import CommunityPage from "./pages/CommunityPage";
import MessagePage from "./pages/MessagePage";
import AllWeatherOpsPage from "./pages/AllWeatherOpsPage";
import DocumentsPage from "./pages/DocumentsPage";
import RosterPage from "./pages/RosterPage";
import AcarsPage from "./pages/AcarsPage";
import ManualFlightReportPage from "./pages/ManualFlightReportPage";
import LedgerPage from "./pages/LedgerPage";
import UserProfilePage from "./pages/UserProfilePage";
import LogbookPage from "./pages/LogbookPage";
import GlobalVisa from "./pages/GlobalVisa";
import ToursAllPage from "./pages/ToursAllPage";
import TourPage from "./pages/TourPage";
import NetworkMap from "./pages/NetworkMap";

import { appVersion } from "./version";
axios.defaults.withCredentials = true;




// Lazy Loading



// import LoginPage from "./pages/LoginPage";
// import ResetEmailPage from "./pages/ResetEmailPage";
// import ResetPasswordPage from "./pages/ResetPasswordPage";
// import ChartersManagePage from "./pages/ChartersManagePage";
// import CharterCreatePage from "./pages/CharterCreatePage";
// import TripPlanner from "./pages/TripPlanner";

const LoginPage = lazy( () => import('./pages/LoginPage') );
const ResetEmailPage = lazy( () => import('./pages/ResetEmailPage') );
const ResetPasswordPage = lazy( () => import('./pages/ResetPasswordPage') );
const ChartersManagePage = lazy( () => import('./pages/ChartersManagePage') );
const CharterCreatePage = lazy( () => import('./pages/CharterCreatePage') );
const TripPlanner = lazy( () => import('./pages/TripPlanner') );



// Admin Pages
const AdminHome = lazy( () => import('./admin/pages/AdminHome') );
const AdminFlights = lazy( () => import('./admin/pages/AdminFlights') );
const AdminFlightReport = lazy( () => import('./admin/pages/AdminFlightReport') );
const AdminViewApps = lazy( () => import('./admin/pages/AdminViewApps') );
const AdminViewAppsHistory = lazy( () => import('./admin/pages/AdminViewAppsHistory') );
const AdminMembers = lazy( () => import('./admin/pages/AdminMembers') );
const AdminMemberDetail = lazy( () => import('./admin/pages/AdminMemberDetail') );
const AdminVatsimLog = lazy( () => import('./admin/pages/AdminVatsimLog') );
const AdminPromotions = lazy( () => import('./admin/pages/AdminPromotions') );
const AdminAWO = lazy( () => import('./admin/pages/AdminAWO') );
const AdminScripts = lazy( () => import('./admin/pages/AdminScripts') );
const AdminRoutes = lazy( () => import('./admin/pages/AdminRoutes') );
const AdminHubs = lazy( () => import('./admin/pages/AdminHubs') );
const Admin24HourLog = lazy( () => import('./admin/pages/Admin24HourLog') );


const AdminTours = lazy( () => import('./admin/pages/AdminTours') );
const AdminTourEdit = lazy( () => import('./admin/pages/AdminTourEdit') );
const AdminStats = lazy( () => import('./admin/pages/AdminStats') );


function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [darkmode, setDarkmode] = useState(() => localStorage.getItem('darkmode') === 'true');
  const [pilotData, setPilotData] = useState(false);
  const [adminCreds, setAdminCreds] = useState(false);

  // Ref-based logout guard: set synchronously in handleAppLogout so that the
  // [loggedIn] useEffect's verifyUser() call (which fires when loggedIn→false)
  // bails out immediately without hitting the network.  Unlike sessionStorage,
  // this lives only in the current React tree and resets after one use.
  const justLoggedOut = useRef(false);

  console.log('Public URL:', process.env.PUBLIC_URL);
  function appTheme(){
    console.log("App.js: appTheme() " + {darkmode})
    setDarkmode(darkmode => {
      const next = !darkmode;
      localStorage.setItem('darkmode', next);
      return next;
    });
  }

 

  useEffect(() => {
    document.title = `FDXVA v${appVersion}`;
  }, []);

  useEffect(() => {
    if (darkmode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [darkmode]);

 
  useEffect(() => {
    if(!loggedIn){
      console.log("App.js useEffect: Not logged in.");
      verifyUser();
    }
  }, [loggedIn])

  // Called by Dashboard via HomePage when the user clicks Logout.
  // Clears all client-side state synchronously, then lets the [loggedIn]
  // useEffect re-fire — verifyUser() will see justLoggedOut.current and bail.
  // sessionStorage key is also set so that a manual page reload after logout
  // (new React tree, new useRef) still skips re-auth.
  const handleAppLogout = () => {
    justLoggedOut.current = true;
    localStorage.clear();
    sessionStorage.setItem('justLoggedOut', '1'); // survives page reload within the tab
    setLoggedIn(false);
    setPilotData(false);
    setAdminCreds(false);
  };


  const fetchUserData = async() => {

    console.log('App.js fetchUserData()');

    try {
      const result = await axios.get('/api/get_si', {
        params: { _: Date.now() }, // cache buster
        headers: { Accept: "application/json" },
      });

      if(result.data){
        localStorage.setItem("userData", JSON.stringify(result.data));
        setPilotData(result.data);

        if(result.data.staff === 1){
          if(result.data.access !== "" && result.data.staff_desc !== ""){
            const x = {"title": result.data.staff_desc, "access": result.data.access};
            setAdminCreds(x);
          }
        }
      }
    }
    catch (e) {
      console.log(e)
    }
    finally{
      setLoading(false); // Stop loading after any error
    }

  }


  const verifyUser = async (obj) => {
    // useRef guard: set synchronously by handleAppLogout.
    // Catches the immediate [loggedIn] effect re-fire within the same React tree.
    if (justLoggedOut.current) {
        justLoggedOut.current = false;
        setLoading(false);
        return;   // skip re-auth immediately after logout
    }
    // sessionStorage guard: survives page reloads within the same browser tab.
    // Key is set by handleAppLogout and cleared only by LoginPage on successful
    // login — NOT here — so that multiple navigations to / after logout are all
    // blocked until the user explicitly logs in again.  
    if (sessionStorage.getItem('justLoggedOut')) {
        setLoading(false);
        return;
    }
    try {
      setLoading(true);
      const result = await axios.get('/api/get_si', {
        params: { _: Date.now() }, // cache buster (optional)
        withCredentials: true,     // needed if authenticator uses cookies/sessions
        headers: { Accept: "application/json" },
      });
      const resObj = result.data;
      console.log(result.data)
      // console.log(result.data.staff_desc)
      if(resObj.login === "OK"){
        // console.log("User verified");
        localStorage.setItem("userData", JSON.stringify(resObj));
        setPilotData(resObj);

        if(resObj.staff === 1){
          if(resObj.access !== "" && resObj.staff_desc !== ""){
            const x = {"title": resObj.staff_desc, "access": resObj.access};
            console.log(x)
            setAdminCreds(x);
          }else{
            console.log("Access and staff_desc are empty. Send to login page.");

          }
        }

        setLoggedIn(true);

      }else{
        console.log("User not verified. Send to login page.");
        //window.location.href = 'https://fdxva.org/public/forms/login.php';
      }

    }
    catch (e) {
      console.log(e)
    }
    finally{
      setLoading(false); // Stop loading after any error
    }
  }
  
  
  return (
    <Suspense fallback={<p>Loading...</p>}>
      {loading ? (
        <div>Authenticating...</div>
      ) : loggedIn ? (
      <AuthContext.Provider value={{ admin: adminCreds, darkmode, appTheme }}>
      <Routes>

          {/* Redirect any 404s back to the React home */}
          {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
          <Route path="/" element={
            <HomePage page={"Home"} iid={pilotData.iid} admin={adminCreds} toggle={appTheme} darkmode={darkmode} refreshUser={fetchUserData} onLogout={handleAppLogout} />
            }
          />
          {/* <Route path="/landing" element={ <Landing  /> } />  not really used right now */}
         {/* <Route path="/login" element={ <LoginPage page={"Login"} darkmode={darkmode} /> } /> */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/routes" element={ <RoutePage page={"Routes"} darkmode={darkmode}/> } />
          <Route path="/loadsheet" element={ <LoadsheetPage page={"Loadsheet"} darkmode={darkmode}/> } />
          <Route path="/inflight" element={ <InFlightPage page={"InFlight"} darkmode={darkmode}/> } />
          <Route path="/operations" element={ <OperationsPage page={"Operations"} darkmode={darkmode} pilotData={pilotData}/> } />
          <Route path="/jumpseat" element={ <JumpseatPage page={"Jumpseat"} darkmode={darkmode} pilotData={pilotData} setPilotData={setPilotData}/> } />
          <Route path="/community" element={ <CommunityPage page={"Community"} darkmode={darkmode}/> } />
          <Route path="/documents" element={ <DocumentsPage page={"Documents"} darkmode={darkmode}/> } />
          <Route path="/roster" element={ <RosterPage page={"Roster"} darkmode={darkmode}/> } />
          <Route path="/acars" element={ <AcarsPage page={"Acars"} darkmode={darkmode}/> } />
          <Route path="/awo" element={ <AllWeatherOpsPage page={"All Weather Ops"} darkmode={darkmode} cat23={pilotData.cat23} /> } />   
          <Route path="/manual-flight-report" element={ <ManualFlightReportPage page={"Manual Flight Report"} darkmode={darkmode} cid={pilotData.vatsim}/> } /> 
          <Route path="/ledger" element={ <LedgerPage page={"Ledger"} darkmode={darkmode}/> } />
          <Route path="/user-profile" element={ <UserProfilePage page={"User Profile"} darkmode={darkmode}/> } />
          <Route path="/reset-email" element={ <ResetEmailPage page={"Change Email"} darkmode={darkmode}/> } />
          <Route path="/reset-password" element={ <ResetPasswordPage page={"Change Password"} darkmode={darkmode}/> } />
          <Route path="/logbook" element={ <LogbookPage page={"Logbook"} darkmode={darkmode}/> } />
          <Route path="/charter-view" element={ <ChartersViewPage page={"Charter View"} darkmode={darkmode} charterCert={pilotData.charterCert}/> } />
          <Route path="/charter-manage" element={ <ChartersManagePage page={"Charter Manage"} darkmode={darkmode} charterCert={pilotData.charterCert} iid={pilotData.iid}/> } />
          <Route path="/charter-create" element={ <CharterCreatePage page={"Charter Creator"} darkmode={darkmode} charterCert={pilotData.charterCert} iid={pilotData.iid}/> } />
          <Route path="/messages" element={ <MessagePage page={"Messages"} darkmode={darkmode} iid={pilotData.iid}/> } />
          <Route path="/visas" element={ <GlobalVisa page={"Global Visa"} darkmode={darkmode} /> } />
          <Route path="/tours" element={ <ToursAllPage page={"Tours"} darkmode={darkmode} /> } />
          <Route path="/tour/:id" element={ <TourPage page={"Tour"} darkmode={darkmode} /> } />
          <Route path="/network-map" element={ <NetworkMap page={"Network Map"} darkmode={darkmode} location={pilotData.location} /> } />
          <Route path="/trip-planner" element={ <TripPlanner page={"Trip Planner"} darkmode={darkmode} /> } />



          {/* Admin Content */}
          <Route path="/admin-home" element={ <AdminHome page={"Admin"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-flights" element={ <AdminFlights page={"AdminFlights"} darkmode={darkmode} admin={adminCreds}/> } />

          <Route path="/admin-flight-report" element={ <Navigate to="/admin-flights" replace /> } />  {/* Fallback in case :id is omitted */}
          <Route path="/admin-flight-report/:id" element={ <AdminFlightReport page={"AdminFlightReport"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-view-apps" element={ <AdminViewApps page={"AdminViewApps"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-view-apps-history" element={ <AdminViewAppsHistory page={"AdminViewAppsHistory"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-members" element={ <AdminMembers page={"AdminMembers"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-member-detail/:iid" element={ <AdminMemberDetail page={"AdminMemberDetail"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-vatsim-log" element={ <AdminVatsimLog page={"AdminVatsimLog"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-promotions/:iid" element={ <AdminPromotions page={"AdminPromotions"} darkmode={darkmode} admin={adminCreds}/> } /> 
          <Route path="/admin-awo/:iid" element={ <AdminAWO page={"AdminAWO"} darkmode={darkmode} admin={adminCreds}/> } />
          <Route path="/admin-scripts" element={ <AdminScripts page={"AdminScripts"} darkmode={darkmode} admin={adminCreds}/> } />

           <Route path="/admin-hubs" element={ <AdminHubs page={"AdminHubs"} darkmode={darkmode} admin={adminCreds}/> } />  
           <Route path="/admin-routes" element={ <AdminRoutes page={"AdminRoutes"} darkmode={darkmode} admin={adminCreds}/> } />  
           <Route path="/admin-stats" element={ <AdminStats page={"AdminStats"} darkmode={darkmode} admin={adminCreds}/> } />  
           <Route path="/admin-tours" element={ <AdminTours page={"AdminTours"} darkmode={darkmode} admin={adminCreds}/> } /> 
           <Route path="/admin-tour-edit/:id" element={ <AdminTourEdit page={"AdminTourEdit"} darkmode={darkmode} admin={adminCreds}/> } /> 
           <Route path="/admin-24hourlog" element={ <Admin24HourLog page={"Admin24HourLog"} darkmode={darkmode} admin={adminCreds}/> } /> 


          

          


      </Routes>
      </AuthContext.Provider>
      ):(
        // Unauthenticated users go here
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </Suspense>


  );
}

export default App;
